/**
 * PURPOSE: Cliente de administración SISDEU (FTR-0017 Phase 4): vínculos de institución, política de
 *   capacidades de compañía y bandeja de alertas.
 * RESPONSIBILITIES: Llamar /api/hayllis/v2/admin/{institution-links,sisdeu-policy,portal-alerts} con
 *   httpService (sesión staff y cabecera X-Hayllis-Company del interceptor de FTR-0012) y devolver el
 *   contrato desenvuelto. Nunca envía ni recibe contraseñas, hashes, documentos ni datos de contacto:
 *   el backend ya los excluye de estas respuestas por diseño (ver FTR-0017 D12).
 * COLLABORATORS: httpService.js, SisdeuInstitutionLinksModal.jsx, SisdeuPolicyModal.jsx,
 *   SisdeuAlertsModal.jsx, portalAccountService.js (acciones SISDEU de una cuenta puntual).
 * ROUTE: winscore/src/services/sisdeuAdminService.js
 */
import httpService from "./httpService.js";

export const INSTITUTION_LINKS_BASE = "/api/hayllis/v2/admin/institution-links";
export const SISDEU_POLICY_BASE = "/api/hayllis/v2/admin/sisdeu-policy";
export const PORTAL_ALERTS_BASE = "/api/hayllis/v2/admin/portal-alerts";

const unwrap = (response) => response?.data?.data ?? {};

export const listInstitutionLinks = async () => {
  const { links } = unwrap(await httpService.get(INSTITUTION_LINKS_BASE));
  return Array.isArray(links) ? links : [];
};

const EMPTY_CANDIDATES = Object.freeze({
  high: [],
  medium: [],
  unmatched: [],
  unproposedExternals: [],
  totals: { institutions: 0, alreadyLinked: 0, high: 0, medium: 0, unmatched: 0, sisdeuInstitutions: 0 },
});

export const getSisdeuCandidates = async () => {
  const data = unwrap(await httpService.get(`${INSTITUTION_LINKS_BASE}/sisdeu/candidates`));
  return { ...EMPTY_CANDIDATES, ...data };
};

/** `links`: [{ institutionId, externalInstitutionId }]. Devuelve { approved, rejected, total }. */
export const approveInstitutionLinks = async (links) => {
  const data = unwrap(await httpService.post(`${INSTITUTION_LINKS_BASE}/sisdeu/approve`, { links }));
  return {
    approved: Array.isArray(data.approved) ? data.approved : [],
    rejected: Array.isArray(data.rejected) ? data.rejected : [],
    total: Number(data.total) || 0,
  };
};

export const unlinkInstitutionSisdeu = async (idinstitution) => {
  const data = unwrap(
    await httpService.delete(`${INSTITUTION_LINKS_BASE}/${encodeURIComponent(String(idinstitution))}/sisdeu`)
  );
  return data.link;
};

export const syncInstitutionSisdeuAccounts = async (idinstitution) => {
  const data = unwrap(
    await httpService.post(`${INSTITUTION_LINKS_BASE}/${encodeURIComponent(String(idinstitution))}/sisdeu/sync-accounts`)
  );
  return { link: data.link, summary: data.summary || {} };
};

export const getSisdeuPolicy = async () => {
  const { capabilities } = unwrap(await httpService.get(SISDEU_POLICY_BASE));
  return Array.isArray(capabilities) ? capabilities : [];
};

/** `enabledCapabilities`: lista de capacidades que deben quedar habilitadas (las demás, deshabilitadas). */
export const updateSisdeuPolicy = async (enabledCapabilities) => {
  const data = unwrap(await httpService.put(SISDEU_POLICY_BASE, { capabilities: enabledCapabilities }));
  return {
    capabilities: Array.isArray(data.capabilities) ? data.capabilities : [],
    affectedAccounts: Number(data.affectedAccounts) || 0,
  };
};

export const PORTAL_ALERT_STATUSES = Object.freeze(["open", "resolved", "all"]);

export const listPortalAlerts = async (status = "open") => {
  const { alerts, total } = unwrap(await httpService.get(PORTAL_ALERTS_BASE, { params: { status } }));
  return { alerts: Array.isArray(alerts) ? alerts : [], total: Number(total) || 0 };
};

export const resolvePortalAlert = async (idalert) => {
  const data = unwrap(await httpService.post(`${PORTAL_ALERTS_BASE}/${encodeURIComponent(String(idalert))}/resolve`));
  return data.alert;
};
